import { api } from ".";

export interface InboundWebhookTagResponse {
  Id: number;
  Key: string;
  Value: string;
  Type: InboundWebhookTagTypeEnum;
}

export interface InboundWebhookLogResponse {
  Id: number;
  InboundWebhookProviderId: number;
  DateUtc: string; // ISO string
  Payload: string;
  Tags: InboundWebhookTagResponse[];
}

export interface PaginatedResponse<T> {
  items: T[];
  totalCount: number;
  pageSize: number;
  currentPage: number;
  totalPages: number;
}

export interface WebhookLogPaginationParams {
  page?: number;
  pageSize?: number;
  providerId?: number;
  providerTypeId?: number; // Maps to providerType in API (InboundWebhookProviderTypeEnum)
  dateRangeStart?: Date;
  dateRangeEnd?: Date;
  sortBy?: string;
  sortDirection?: number; // 0 = Ascending, 1 = Descending
}

// Enums to match the backend
export enum InboundWebhookProviderTypeEnum {
  OCR = 1,
  Manual = 2,
}

export enum SortDirection {
  Ascending = 0,
  Descending = 1
}

export enum InboundWebhookTagTypeEnum {
  Text = 1,
  File = 2
}

const webhookLogsUrlV1 = "/v1/InboundWebhookLogs";
const webhookLogsCountUrlV1 = "/v1/InboundWebhookLogs/count";

// Get total count of webhook logs (if endpoint exists)
export const getWebhookLogsCount = async (params: WebhookLogPaginationParams = {}): Promise<number> => {
  const {
    providerId,
    providerTypeId,
    dateRangeStart,
    dateRangeEnd,
  } = params;
  
  try {
    const queryParams = new URLSearchParams();
    
    if (providerId !== undefined) {
      queryParams.append('providerId', providerId.toString());
    }
    
    if (providerTypeId !== undefined) {
      queryParams.append('providerType', providerTypeId.toString());
    }
    
    if (dateRangeStart) {
      queryParams.append('fromDate', dateRangeStart.toISOString());
    }
    
    if (dateRangeEnd) {
      queryParams.append('toDate', dateRangeEnd.toISOString());
    }
    
    const url = `${webhookLogsCountUrlV1}?${queryParams.toString()}`;
    const response = await api.get<number>(url);
    return response?.data || 0;
  } catch (error) {
    // If count endpoint doesn't exist, return -1 to indicate unknown
    console.warn('Count endpoint not available, using estimation');
    return -1;
  }
};

export const getAllInboundWebhookLogs = async (params: WebhookLogPaginationParams = {}): Promise<PaginatedResponse<InboundWebhookLogResponse>> => {
  const {
    page = 1,
    pageSize = 25,
    providerId,
    providerTypeId,
    dateRangeStart,
    dateRangeEnd,
    sortBy = "DateUtc",
    sortDirection = SortDirection.Descending
  } = params;
  
  // Build query parameters
  const queryParams = new URLSearchParams();
  
  if (providerId !== undefined) {
    queryParams.append('providerId', providerId.toString());
  }
  
  if (providerTypeId !== undefined) {
    queryParams.append('providerType', providerTypeId.toString());
  }
  
  if (dateRangeStart) {
    queryParams.append('fromDate', dateRangeStart.toISOString());
  }
  
  if (dateRangeEnd) {
    queryParams.append('toDate', dateRangeEnd.toISOString());
  }
  
  queryParams.append('pageSize', pageSize.toString());
  queryParams.append('currentPage', page.toString());
  queryParams.append('sortBy', sortBy);
  queryParams.append('sortDirection', sortDirection.toString());
  
  const url = `${webhookLogsUrlV1}?${queryParams.toString()}`;
  
  try {
    const response = await api.get<any>(url);
    
    // Check if the response is paginated (has totalCount) or just an array
    if (response?.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
      // Response is a paginated object with metadata
      const { items, totalCount, currentPage: returnedPage, pageSize: returnedPageSize } = response.data;
      const calculatedTotalPages = Math.ceil(totalCount / (returnedPageSize || pageSize));
      
      return {
        items: items || [],
        totalCount: totalCount || 0,
        pageSize: returnedPageSize || pageSize,
        currentPage: returnedPage || page,
        totalPages: calculatedTotalPages || 1
      };
    } else if (Array.isArray(response?.data)) {
      // Response is just an array - we need to handle pagination client-side
      const items = response.data;
      
      // Check if we got a full page of results
      const hasMorePages = items.length === pageSize;
      
      // For array responses, we can't know the exact total count
      // We'll need to estimate based on whether we got a full page
      const estimatedTotalCount = hasMorePages ? (page * pageSize) + 1 : ((page - 1) * pageSize) + items.length;
      const estimatedTotalPages = hasMorePages ? page + 1 : page;
      
      return {
        items,
        totalCount: estimatedTotalCount,
        pageSize,
        currentPage: page,
        totalPages: estimatedTotalPages
      };
    } else {
      // Fallback for unexpected response format
      console.warn('Unexpected response format from webhook logs API:', response);
      return {
        items: [],
        totalCount: 0,
        pageSize,
        currentPage: page,
        totalPages: 1
      };
    }
  } catch (error) {
    console.error('Error fetching webhook logs:', error);
    throw error;
  }
};

